import { useState, useEffect, useRef, useCallback } from "react";
import { Text, Group, Box, ScrollArea, Paper } from "@mantine/core";
import { IconPencil, IconPlus, IconChevronLeft, IconChevronRight } from "@tabler/icons-react";
import { Input, Button } from "@onengine/component-library";
import { useFormStore } from "@/store/form-builder";
import { SectionEditor } from "./SectionEditor";
import { INLINE_HIT_AREA } from "../../data/builderLabels";

function InlinePageNameInput({
  value,
  onChange,
  onClose,
}: {
  value: string;
  onChange: (val: string) => void;
  onClose: () => void;
}) {
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const input = wrapperRef.current?.querySelector("input");
    if (input) input.focus();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === "Escape") onClose();
  };

  return (
    <Box ref={wrapperRef} onKeyDown={handleKeyDown}>
      <Input value={value} onChange={onChange} size="small" />
    </Box>
  );
}

export function FormEditor() {
  const {
    pages,
    currentPageIndex,
    setCurrentPageIndex,
    updatePage,
    addSection,
    setSelectedQuestionId,
    selectedSectionId,
  } = useFormStore();
  const page = pages[currentPageIndex];
  const [editingPageName, setEditingPageName] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});

  const closePageNameEdit = useCallback(() => setEditingPageName(false), []);

  useEffect(() => {
    if (page) {
      const m: Record<string, boolean> = {};
      page.sections.forEach((s) => {
        m[s.id] = true;
      });
      setExpandedSections(m);
    }
  }, [currentPageIndex]);

  useEffect(() => {
    if (!selectedSectionId || !page) return;
    const collapsed: Record<string, boolean> = {};
    page.sections.forEach((s) => {
      collapsed[s.id] = s.id === selectedSectionId;
    });
    setExpandedSections(collapsed);
    const el = document.querySelector(`[data-section-id="${selectedSectionId}"]`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [selectedSectionId]);
  if (!page) return null;

  const handleBackgroundClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    // Ignore clicks from portaled content (Mantine Menu/Popover) — those bubble
    // through React's tree but live outside this DOM subtree, and selecting a
    // field via "Add Manually" would otherwise be immediately undone here.
    if (!e.currentTarget.contains(target)) return;
    if (!target.closest("[data-qcard]")) setSelectedQuestionId(null);
  };

  return (
    <ScrollArea style={{ flex: 1 }} p="lg" onClick={handleBackgroundClick}>
      <Box maw={720} mx="auto">
        <Group mb="md" gap={8}>
          {editingPageName ? (
            <InlinePageNameInput
              value={page.name}
              onChange={(val) => updatePage(page.id, { name: val })}
              onClose={closePageNameEdit}
            />
          ) : (
            <>
              <Paper
                py={5}
                radius="xl"
                withBorder
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  cursor: "pointer",
                  background: "none",
                  border: "none",
                }}
                onClick={() => setEditingPageName(true)}
              >
                <Text size="20px" fw={600} c="#131927">
                  {currentPageIndex + 1}: {page.name}
                </Text>
                <Button
                  type="icon"
                  variant="tertiary"
                  size="small"
                  iconLeft={<IconPencil size={20} />}
                  onClick={() => setEditingPageName(true)}
                  htmlAttributes={{ "aria-label": "Rename page", ...INLINE_HIT_AREA }}
                />
              </Paper>
            </>
          )}
          {pages.length > 1 && (
            <Group gap={6} ml="auto">
              <Button
                type="icon"
                variant="tertiary"
                size="small"
                isDisabled={currentPageIndex === 0}
                iconLeft={<IconChevronLeft size={16} />}
                onClick={() => setCurrentPageIndex(currentPageIndex - 1)}
                style={{ border: "none", background: "none" }}
              />
              <Text size="sm" fw={500} c="dimmed" style={{ whiteSpace: "nowrap" }}>
                Page {currentPageIndex + 1} of {pages.length}
              </Text>
              <Button
                type="icon"
                variant="tertiary"
                size="small"
                isDisabled={currentPageIndex === pages.length - 1}
                iconLeft={<IconChevronRight size={16} />}
                onClick={() => setCurrentPageIndex(currentPageIndex + 1)}
                style={{ border: "none", background: "none" }}
              />
            </Group>
          )}
        </Group>

        {page.sections.map((sc) => (
          <SectionEditor
            key={sc.id}
            section={sc}
            pageId={page.id}
            isExpanded={expandedSections[sc.id] !== false}
            onToggle={() => setExpandedSections((p) => ({ ...p, [sc.id]: !(p[sc.id] !== false) }))}
          />
        ))}

        <Button
          variant="neutral-secondary"
          size="base"
          onClick={() => addSection(page.id)}
          iconLeft={<IconPlus size={20} />}
          style={{ width: "100%", marginTop: 24 }}
        >
          New Section
        </Button>
      </Box>
    </ScrollArea>
  );
}
